import { useState, useEffect, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import {
  ArrowUp, ArrowDown, PencilSimple, X, CheckCircle,
  XCircle, ArrowClockwise, CurrencyDollar, Brain, Warning
} from "@phosphor-icons/react";
import { toast } from "sonner";

/* ─── helpers ──────────────────────────────────────────────── */
const PROVIDER_COLORS = {
  openai:    "bg-emerald-500/15 text-emerald-400 border-emerald-500/20",
  anthropic: "bg-amber-500/15  text-amber-400  border-amber-500/20",
  groq:      "bg-sky-500/15    text-sky-400    border-sky-500/20",
  gemini:    "bg-purple-500/15 text-purple-400 border-purple-500/20",
  local:     "bg-zinc-500/15   text-zinc-400   border-zinc-500/20",
};

const CB_BADGE = {
  closed:    { label: "Closed",    cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" },
  open:      { label: "Open",      cls: "bg-red-500/15     text-red-400     border-red-500/20"     },
  half_open: { label: "Half-Open", cls: "bg-amber-500/15   text-amber-400   border-amber-500/20"  },
};

function providerColor(p) {
  return PROVIDER_COLORS[p] || PROVIDER_COLORS.local;
}

function fmt(n, decimals = 4) {
  if (n == null) return "—";
  return Number(n).toFixed(decimals);
}

function SpendBar({ spent, limit }) {
  if (!limit || limit <= 0) return <span className="text-xs text-zinc-600 italic">No limit</span>;
  const pct = Math.min(100, (spent / limit) * 100);
  const color = pct >= 90 ? "bg-red-500" : pct >= 70 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="w-full">
      <div className="flex justify-between mb-1">
        <span className="text-[10px] text-zinc-500">${fmt(spent, 2)} spent</span>
        <span className="text-[10px] text-zinc-500">${fmt(limit, 2)} limit</span>
      </div>
      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* ─── EditModal ─────────────────────────────────────────────── */
function EditModal({ cfg, usageRich, onSave, onClose }) {
  const [form, setForm] = useState({
    enabled:                  cfg.enabled,
    monthly_token_limit:      cfg.monthly_token_limit ?? 0,
    cost_per_1k_tokens_input:  cfg.cost_per_1k_tokens_input ?? 0,
    cost_per_1k_tokens_output: cfg.cost_per_1k_tokens_output ?? 0,
    max_tokens_per_call:       cfg.max_tokens_per_call ?? 4096,
    timeout_seconds:           cfg.timeout_seconds ?? 30,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const updates = {
      enabled:                  form.enabled,
      monthly_token_limit:      Number(form.monthly_token_limit),
      cost_per_1k_tokens_input:  Number(form.cost_per_1k_tokens_input),
      cost_per_1k_tokens_output: Number(form.cost_per_1k_tokens_output),
      max_tokens_per_call:       Number(form.max_tokens_per_call),
      timeout_seconds:           Number(form.timeout_seconds),
    };
    const ok = await onSave(cfg.provider, updates);
    setSaving(false);
    if (ok) onClose();
  };

  const monthSpent = usageRich?.month?.cost_usd ?? 0;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-[#111111] border border-white/10 rounded-xl w-full max-w-lg shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <div className="flex items-center gap-3">
            <span className={`text-xs font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${providerColor(cfg.provider)}`}>
              {cfg.provider}
            </span>
            <span className="text-sm font-medium text-white font-mono">{cfg.model_name}</span>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* enabled toggle */}
          <div className="flex items-center justify-between">
            <label className="text-sm text-zinc-300">Provider Enabled</label>
            <button
              onClick={() => set("enabled", !form.enabled)}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.enabled ? "bg-amber-500" : "bg-zinc-700"}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${form.enabled ? "translate-x-5" : "translate-x-0"}`} />
            </button>
          </div>

          {/* cost fields */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Cost / 1k Input Tokens ($)" type="number" step="0.0001"
              value={form.cost_per_1k_tokens_input}
              onChange={v => set("cost_per_1k_tokens_input", v)} />
            <Field label="Cost / 1k Output Tokens ($)" type="number" step="0.0001"
              value={form.cost_per_1k_tokens_output}
              onChange={v => set("cost_per_1k_tokens_output", v)} />
          </div>

          {/* limits */}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Max Tokens / Call" type="number"
              value={form.max_tokens_per_call}
              onChange={v => set("max_tokens_per_call", v)} />
            <Field label="Timeout (seconds)" type="number"
              value={form.timeout_seconds}
              onChange={v => set("timeout_seconds", v)} />
          </div>

          {/* monthly limit with live spend bar */}
          <div>
            <Field label="Monthly Token Limit (0 = unlimited)" type="number"
              value={form.monthly_token_limit}
              onChange={v => set("monthly_token_limit", v)} />
            <div className="mt-3">
              <SpendBar spent={monthSpent} limit={Number(form.monthly_token_limit)} />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/5 transition-all">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-semibold bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-50 transition-all"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step }) {
  return (
    <div>
      <label className="block text-xs text-zinc-500 mb-1">{label}</label>
      <input
        type={type}
        step={step}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 transition-colors font-mono"
      />
    </div>
  );
}

/* ─── LLM Engine Aliases card ───────────────────────────────── */
function LLMEngineAliasCard({ fetchLLMEngineConfig, saveLLMEngineConfig }) {
  const [form, setForm]     = useState({ gemini_model: '', groq_model: '' });
  const [saved, setSaved]   = useState({ gemini_model: '', groq_model: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLLMEngineConfig().then(d => {
      if (d) { setForm(d); setSaved(d); }
    });
  }, [fetchLLMEngineConfig]);

  const dirty = form.gemini_model !== saved.gemini_model || form.groq_model !== saved.groq_model;

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveLLMEngineConfig(form);
      setSaved(form);
      toast.success('LLM engine aliases updated');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="jp-card p-5 space-y-4">
      <h3 className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 flex items-center gap-2">
        <Brain size={12} /> LLM Engine Aliases
      </h3>
      <p className="text-xs text-zinc-600">Model alias strings passed directly to the AI scoring engine. Changing these takes effect on the next scored search.</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Primary (Gemini)</label>
          <input
            type="text"
            value={form.gemini_model}
            onChange={e => setForm(f => ({ ...f, gemini_model: e.target.value }))}
            placeholder="e.g. gemini-2.5-flash"
            className="w-full bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-sm text-white font-mono placeholder-zinc-700 focus:outline-none focus:border-purple-500/50"
          />
        </div>
        <div>
          <label className="text-[10px] text-zinc-500 uppercase tracking-wider block mb-1">Fallback (Groq)</label>
          <input
            type="text"
            value={form.groq_model}
            onChange={e => setForm(f => ({ ...f, groq_model: e.target.value }))}
            placeholder="e.g. llama-3.1-8b-instant"
            className="w-full bg-white/5 border border-white/8 rounded-lg px-3 py-2 text-sm text-white font-mono placeholder-zinc-700 focus:outline-none focus:border-sky-500/50"
          />
        </div>
      </div>
      {dirty && (
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 text-xs font-bold bg-amber-500 text-black rounded-lg hover:bg-amber-400 disabled:opacity-50 transition-colors"
        >
          {saving ? 'Saving…' : 'Save Aliases'}
        </button>
      )}
    </div>
  );
}

/* ─── Main component ────────────────────────────────────────── */
export default function AdminLLMTab() {
  const { fetchLLMConfig, updateLLMConfig, fetchAdminTelemetry, fetchLLMEngineConfig, saveLLMEngineConfig } = useApp();

  const [configs, setConfigs] = useState([]);
  const [usageMap, setUsageMap] = useState({});   // provider → rich usage obj
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [editTarget, setEditTarget] = useState(null);  // cfg object being edited

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true); else setRefreshing(true);
    try {
      const [cfgData, usageData] = await Promise.all([
        fetchLLMConfig(),
        fetchAdminTelemetry("usage"),
      ]);
      if (cfgData) setConfigs(cfgData);
      if (usageData?.providers_rich) {
        const map = {};
        usageData.providers_rich.forEach(p => { map[p.provider] = p; });
        setUsageMap(map);
      }
    } catch (e) {
      toast.error("Failed to load LLM config");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [fetchLLMConfig, fetchAdminTelemetry]);

  useEffect(() => { load(); }, [load]);

  /* toggle enabled */
  const handleToggle = async (cfg) => {
    const ok = await updateLLMConfig(cfg.provider, { enabled: !cfg.enabled });
    if (ok) {
      setConfigs(cs => cs.map(c => c.provider === cfg.provider ? { ...c, enabled: !c.enabled } : c));
      toast.success(`${cfg.provider} ${!cfg.enabled ? "enabled" : "disabled"}`);
    } else {
      toast.error("Update failed");
    }
  };

  /* priority reorder */
  const handleReorder = async (idx, dir) => {
    const next = [...configs];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= next.length) return;
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    // recalc priority_order
    const updated = next.map((c, i) => ({ ...c, priority_order: i + 1 }));
    setConfigs(updated);
    // persist each changed provider
    await Promise.all([
      updateLLMConfig(updated[idx].provider, { priority_order: updated[idx].priority_order }),
      updateLLMConfig(updated[swapIdx].provider, { priority_order: updated[swapIdx].priority_order }),
    ]);
  };

  /* save from edit modal */
  const handleSave = async (provider, updates) => {
    const ok = await updateLLMConfig(provider, updates);
    if (ok) {
      setConfigs(cs => cs.map(c => c.provider === provider ? { ...c, ...updates } : c));
      toast.success(`${provider} config updated`);
      return true;
    } else {
      toast.error("Save failed");
      return false;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-5 h-5 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
        <span className="ml-3 text-sm text-zinc-500">Loading LLM configuration…</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* toolbar */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-white">LLM Provider Configuration</h2>
          <p className="text-xs text-zinc-500 mt-0.5">Manage model costs, limits, and priority order for the circuit-breaker pool.</p>
        </div>
        <button
          onClick={() => load(true)}
          disabled={refreshing}
          className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-zinc-400 hover:text-white hover:bg-white/5 border border-white/5 hover:border-white/10 transition-all disabled:opacity-50"
        >
          <ArrowClockwise size={15} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      {/* LLM engine alias strings */}
      <LLMEngineAliasCard fetchLLMEngineConfig={fetchLLMEngineConfig} saveLLMEngineConfig={saveLLMEngineConfig} />

      {/* summary spend row */}
      <div className="grid grid-cols-4 gap-4">
        {["today", "week", "month"].map(period => {
          const total = Object.values(usageMap).reduce((sum, p) => sum + (p[period]?.cost_usd ?? 0), 0);
          const calls  = Object.values(usageMap).reduce((sum, p) => sum + (p[period]?.calls    ?? 0), 0);
          return (
            <div key={period} className="jp-card p-4">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{period} spend</div>
              <div className="text-xl font-mono font-bold text-amber-400">${fmt(total, 3)}</div>
              <div className="text-xs text-zinc-600 mt-0.5">{calls} calls</div>
            </div>
          );
        })}
        <div className="jp-card p-4">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Active Providers</div>
          <div className="text-xl font-mono font-bold text-white">
            {configs.filter(c => c.enabled).length} / {configs.length}
          </div>
          <div className="text-xs text-zinc-600 mt-0.5">in rotation</div>
        </div>
      </div>

      {/* provider table */}
      <div className="jp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-white/5 text-[10px] text-zinc-500 uppercase tracking-wider">
              <th className="text-left px-5 py-3 w-8">#</th>
              <th className="text-left px-5 py-3">Provider / Model</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Today</th>
              <th className="text-right px-4 py-3">7 Days</th>
              <th className="text-right px-4 py-3">30 Days</th>
              <th className="text-left px-4 py-3">Monthly Spend</th>
              <th className="text-left px-4 py-3">CB State</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {configs.map((cfg, idx) => {
              const rich = usageMap[cfg.provider] ?? {};
              const cb = CB_BADGE[rich.cb_state] ?? CB_BADGE.closed;
              const monthSpent = rich.month?.cost_usd ?? 0;

              return (
                <tr key={cfg.provider} className="hover:bg-white/[0.02] transition-colors group">
                  {/* priority arrows */}
                  <td className="px-3 py-4">
                    <div className="flex flex-col items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        disabled={idx === 0}
                        onClick={() => handleReorder(idx, -1)}
                        className="text-zinc-600 hover:text-amber-400 disabled:opacity-20 transition-colors"
                      >
                        <ArrowUp size={12} weight="bold" />
                      </button>
                      <button
                        disabled={idx === configs.length - 1}
                        onClick={() => handleReorder(idx, 1)}
                        className="text-zinc-600 hover:text-amber-400 disabled:opacity-20 transition-colors"
                      >
                        <ArrowDown size={12} weight="bold" />
                      </button>
                    </div>
                  </td>

                  {/* provider / model */}
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider ${providerColor(cfg.provider)}`}>
                        {cfg.provider}
                      </span>
                      <div>
                        <div className="text-white font-medium font-mono text-xs">{cfg.model_name}</div>
                        <div className="text-[10px] text-zinc-600">
                          ${fmt(cfg.cost_per_1k_tokens_input)} / ${fmt(cfg.cost_per_1k_tokens_output)} per 1k
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* enabled toggle */}
                  <td className="px-4 py-4">
                    <button
                      onClick={() => handleToggle(cfg)}
                      title={cfg.enabled ? "Disable provider" : "Enable provider"}
                      className="transition-colors"
                    >
                      {cfg.enabled
                        ? <CheckCircle size={20} weight="fill" className="text-emerald-400 hover:text-emerald-300" />
                        : <XCircle    size={20} weight="fill" className="text-red-400     hover:text-red-300"    />
                      }
                    </button>
                  </td>

                  {/* today calls / cost */}
                  <td className="px-4 py-4 text-right">
                    <div className="text-xs text-white font-mono">{rich.today?.calls ?? 0}</div>
                    <div className="text-[10px] text-zinc-600">${fmt(rich.today?.cost_usd ?? 0, 4)}</div>
                  </td>

                  {/* 7d */}
                  <td className="px-4 py-4 text-right">
                    <div className="text-xs text-white font-mono">{rich.week?.calls ?? 0}</div>
                    <div className="text-[10px] text-zinc-600">${fmt(rich.week?.cost_usd ?? 0, 3)}</div>
                  </td>

                  {/* 30d */}
                  <td className="px-4 py-4 text-right">
                    <div className="text-xs text-white font-mono">{rich.month?.calls ?? 0}</div>
                    <div className="text-[10px] text-zinc-600">${fmt(rich.month?.cost_usd ?? 0, 3)}</div>
                  </td>

                  {/* monthly spend bar */}
                  <td className="px-4 py-4 min-w-[140px]">
                    <SpendBar spent={monthSpent} limit={cfg.monthly_token_limit} />
                  </td>

                  {/* CB state */}
                  <td className="px-4 py-4">
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded border ${cb.cls}`}>
                      {cb.label}
                    </span>
                  </td>

                  {/* edit */}
                  <td className="px-4 py-4">
                    <button
                      onClick={() => setEditTarget(cfg)}
                      className="opacity-0 group-hover:opacity-100 flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs text-zinc-400 hover:text-white hover:bg-white/8 border border-transparent hover:border-white/10 transition-all"
                    >
                      <PencilSimple size={13} />
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {configs.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <Brain size={36} className="text-zinc-700 mb-3" />
            <p className="text-sm text-zinc-500">No LLM providers configured.</p>
            <p className="text-xs text-zinc-600 mt-1">Run <code className="font-mono text-amber-500">init_db.py</code> to seed default configurations.</p>
          </div>
        )}
      </div>

      {/* cost info footer */}
      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/5 border border-amber-500/10">
        <Warning size={15} weight="fill" className="text-amber-500 mt-0.5 shrink-0" />
        <p className="text-xs text-zinc-500">
          Cost figures are computed from token usage logs multiplied by the per-1k-token rates above.
          Actual billing may differ — verify against your provider dashboard.
          Priority order controls the circuit-breaker fallback sequence (top = highest priority).
        </p>
      </div>

      {/* edit modal */}
      {editTarget && (
        <EditModal
          cfg={editTarget}
          usageRich={usageMap[editTarget.provider]}
          onSave={handleSave}
          onClose={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}
